// external libs
import { Box, Button, Grid, IconButton, makeStyles, styled, SvgIcon, Typography } from '@material-ui/core'
import _ from 'lodash'

// components
import MyButton from '../../../../control/button/button'
import Dialog from '../../../../control/dialog/dialog'

// hooks
import useTranslate from '../../../../i18n/useTranslate'

// utils
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../utilities/date'
import { getFileIconPath } from '../../../../utilities/pathImg'

import type { FileInfo } from '../../../../services/web/types/requestWebServiceTypes'
import { useRef, useState } from 'react'
import useTempFile from '../../../../hooks/useTempFile'
import { ReactComponent as DocumentIcon } from '../../../../images/document.svg'
import useWebService from '../../../../services/web/useWebService'

export interface DocPreviewProps {
    fileInfo: FileInfo
    editable?: boolean
    disabled?: boolean
    onDelete?: () => void
    onChange?: (fileInfo: FileInfo) => void
}

const ACCEPT = '.png,.jpeg,.tiff,.pdf,.doc,.docx'

const DocPreview = (props: DocPreviewProps) => {
    const { fileInfo, editable, disabled, onDelete, onChange } = props

    const callWebService = useWebService()

    const inputRef = useRef<HTMLInputElement | null>(null)
    const translate = useTranslate()
    const tempFile = useTempFile({ accept: ACCEPT })
    const classes = useStyles()
    const fileHistoryDialogRef = useRef<Record<string, Function>>()
    const [isUploading, setIsUploading] = useState<boolean>(false)
    const [fileHistory, setFileHistory] = useState<any[]>([])
    const [tempFileInfo, setTempFileInfo] = useState<FileInfo | null>(null)

    const getDisplayDate = (date?: string) => {
        let display = ''
        if (date) {
            display =
                translate.languageStore.currentLanguage === translate.languageKey.th
                    ? 'ล่าสุดวันที่ ' + convertDateToFormatTH(date)
                    : 'Last updated ' + convertDateToFormatEN(date)
        }
        return display
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setIsUploading(true)
            const files = e.target.files

            if (!files || files.length === 0) {
                return
            }

            const file = files[0]

            const isValid = tempFile.validate(file)
            if (!isValid) {
                return
            }

            const fileUploaded = await tempFile.upload(file)
            if (!fileUploaded) {
                return
            }

            const updatedFileInfo = {
                ...fileUploaded,
                replaceFileID: fileInfo.fileID,
                date: new Date().toISOString(),
            } as FileInfo

            setTempFileInfo(_.cloneDeep(fileInfo))

            onChange?.(updatedFileInfo)
        } catch (error) {
            console.error('Has an error while adding file to fileInfo: ', error)
        } finally {
            if (inputRef.current) {
                inputRef.current.value = ''
            }
            setIsUploading(false)
        }
    }

    const handleCancel = () => {
        if (!tempFileInfo) return
        onChange?.(tempFileInfo)
        setTempFileInfo(null)
    }

    const fetchFileHistory = async (fileId: number, fileReplaceId?: number) => {
        try {
            const { result } = await callWebService.getAdditionalFileHistory(fileId, fileReplaceId)

            setFileHistory(result ?? [])
        } catch (error) {
            console.error('Error while fetching additional doc file history.', error)
        }
    }

    const openFileHistoryDialog = (fileId: number, fileReplaceId?: number) => {
        fetchFileHistory(fileId, fileReplaceId)
        fileHistoryDialogRef?.current?.openDialog()
    }

    const closeFileHistoryDialog = () => {
        fileHistoryDialogRef?.current?.closeDialog()
    }

    return (
        <Box>
            <Box className={classes.filePreview}>
                <img src={process.env.PUBLIC_URL + getFileIconPath(fileInfo.filename)} alt="file icon" />
                <Box className="file-info">
                    <a href={fileInfo.path} target="_blank" rel="noreferrer">
                        <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                            {fileInfo.filename}
                        </Typography>
                    </a>

                    {!!fileInfo.replaceFileID ? (
                        <>
                            <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                                {getDisplayDate(fileInfo.date)}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => openFileHistoryDialog(fileInfo.fileID, fileInfo.replaceFileID)}
                            >
                                <SvgIcon fontSize="small" component={DocumentIcon} />
                            </IconButton>
                        </>
                    ) : null}
                </Box>

                {!disabled ? (
                    <Box className="actions" display="flex" alignItems="center" style={{ gap: 8 }}>
                        <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={handleFileChange} />
                        {editable && !tempFileInfo ? (
                            <MyButton
                                size="small"
                                variant="contained"
                                className={classes.editfileBtn}
                                disabled={isUploading}
                                onClick={() => inputRef.current?.click()}
                            >
                                {translate.get('BUTTON.EDIT')}
                            </MyButton>
                        ) : null}

                        {tempFileInfo ? (
                            <MyButton size="small" variant="contained" color="secondary" onClick={handleCancel}>
                                {translate.get('BUTTON.CANCEL')}
                            </MyButton>
                        ) : (
                            <MyButton size="small" variant="contained" color="secondary" onClick={onDelete}>
                                {translate.get('BUTTON.DELETE')}
                            </MyButton>
                        )}
                    </Box>
                ) : null}
            </Box>

            <Dialog
                maxWidth="lg"
                onRef={(funcs) => (fileHistoryDialogRef.current = funcs)}
                type=""
                title={translate.get('LABEL.HISTORY_FILE_INFORMATION')}
                content={
                    <>
                        <Grid container justify="center" spacing={1}>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_NAME')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_TIME')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_UPLOAD_BY')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_UPLOAD_DATE')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {fileHistory.map((history, index) => (
                                <Grid item xs={12} key={history.fileID}>
                                    <Grid container>
                                        <Grid container item xs={6} alignItems="center">
                                            <img
                                                src={process.env.PUBLIC_URL + getFileIconPath(history.filename)}
                                                alt="file icon"
                                            />

                                            <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                                                <a href={history.path} target="_blank" rel="noreferrer">
                                                    {history.fileName}
                                                </a>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            {fileHistory.length - index}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {history.updatedBy}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {getDisplayDate(history.updatedDate)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid item xs="auto">
                                <pre />
                                <CustomButton onClick={closeFileHistoryDialog}>
                                    {translate.get('BUTTON.CLOSE_MODAL')}
                                </CustomButton>
                            </Grid>
                        </Grid>
                    </>
                }
            />
        </Box>
    )
}

const CustomButton = styled(Button)({
    border: '1px solid #DB5F8E',
    boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
})

const useStyles = makeStyles(() => ({
    filePreview: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingBottom: 8,

        '&:not(:last-child)': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },

        '& img': {
            width: 24,
            height: 24,
            flexShrink: 0,
        },
        '& .file-info': {
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
        },
        '& .actions button': {
            flexShrink: 0,
            minWidth: '64px !important',
        },
    },

    editfileBtn: {
        backgroundColor: '#e57600',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#ff8300',
        },
    },
}))

export default DocPreview
